import React, { useEffect, useRef, useState } from 'react';
import {
  EmailInput,
  useEmailInput,
} from '../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import TextInput from '../../../common/components/TextInput';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import {
  PrimaryButton,
  ButtonColor,
} from '../../../common/buttons/PrimaryButton/PrimaryButton';
import declinedContactsCRUDActions from '@mm/state/actions/DeclinedContactsCRUDActions';

import styles from './OptOutForm.module.css';
import CheckBox from '../../../common/components/Checkbox';
import { Alert } from '@mm/components';
import { ColorType } from '@mm/utils';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useQuery } from '@mm/hooks';

export const OptOutForm = () => {
  const [zipCode, setZipCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [agentCheck, setAgentCheck] = useState(false);
  const { emailState, onChange } = useEmailInput();
  const [alertContent, setAlertContent] = useState('');
  const [alertType, setAlertType] = useState<ColorType | undefined>();
  const hCaptchaRef = useRef<HCaptcha>(null);
  const { openNewInfoModal } = useModalActions();
  const query = useQuery();

  useEffect(() => {
    const email = query.get('email');
    const token = query.get('token');

    const confirmOptOut = async (email: string, token: string) => {
      const response = await declinedContactsCRUDActions.confirm(email, token);

      if (response.success) {
        openNewInfoModal({
          title: 'Please confirm your Opt-Out',
          description: (
            <div>
              <p>
                Your information has been submitted and will be reviewed by our
                legal team for compliance.
              </p>
              <p>This may take up to 30 days to complete.</p>
            </div>
          ),
          button: 'Ok',
        });
      } else {
        openNewInfoModal({
          title: 'Oops, Something went wrong',
          description: response.errors.join(' '),
          button: 'Ok',
        });
      }
    };

    if (token && email) {
      confirmOptOut(email, token);
    }
  }, []);

  const resetCaptcha = () => {
    hCaptchaRef.current?.resetCaptcha();
    setCaptchaToken('');
  };

  const onSubmit = async e => {
    e.preventDefault();
    if (emailState.isValid) {
      const { email } = emailState;
      const response = await declinedContactsCRUDActions.create(
        email,
        captchaToken,
        zipCode,
      );

      const warningErrorCodes = ['already_confirmed', 'already_sent'];

      setAlertType(
        response.success
          ? 'success'
          : warningErrorCodes.includes(response?.error_code)
          ? 'warning'
          : 'error',
      );

      if (response.success) {
        openNewInfoModal({
          title: 'Please confirm your Opt-Out',
          description: 'We will send you confirmation email.',
          button: 'Ok',
        });
      } else {
        switch (response.error_code) {
          case 'already_confirmed':
            setAlertContent('This email has already been added.');
            break;
          case 'already_sent':
            setAlertContent('Confirmation has already been sent.');
            break;
          case 'too_many_requests':
            setAlertContent(
              'You have reached the request limit. Please try again later.',
            );
            break;
          default:
            setAlertContent('Something went wrong. Please try again later.');
        }
      }

      resetCaptcha();
      setTimeout(() => {
        setAlertContent('');
      }, 5000);
    }
  };

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Email Address</label>
        <EmailInput
          onChange={onChange}
          inputProps={{
            className: styles.inputTextField,
            placeholder: '',
          }}
          withIcon={false}
        />
      </div>
      <div className={styles.inputGroup}>
        <label className={styles.inputLabel}>Zip Code</label>
        <TextInput
          className={styles.inputTextField}
          placeholder=""
          aria-label="text"
          onChange={e => setZipCode(e.target.value)}
          type="text"
          style={{ border: '1px solid var(--color-gray-5)' }}
        />
      </div>

      <div className={styles.inputGroup}>
        <CheckBox
          isChecked={agentCheck}
          label="I am an authorized agent submitting the request on behalf of the user."
          checkboxLabelTitleClassName={styles.checkboxLabel}
          onToggle={e => setAgentCheck(e.target.checked)}
        />
      </div>

      <div className={styles.inputGroup}>
        <HCaptcha
          ref={hCaptchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={token => setCaptchaToken(token)}
        />
      </div>

      <div className={styles.inputGroup}>
        <PrimaryButton
          disabled={!captchaToken || !agentCheck || !emailState.isValid}
          className={styles.inputSubmit}
          color={ButtonColor.blue}
          type="submit"
        >
          Opt-Out
        </PrimaryButton>
      </div>
      {alertContent && (
        <Alert type={alertType} showCloseIcon={false}>
          {alertContent}
        </Alert>
      )}
    </form>
  );
};
